import { useState, useEffect, useCallback } from "react";
import { ArrowLeft, Leaf, Beaker, Bug, Sprout, Sparkles, Clock, Droplets, Calculator, FileText, Pencil, FlaskConical, Wrench, MoreHorizontal, ChevronDown, Carrot, Copy, ListOrdered, Waves } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { authFetch } from "@/lib/api";

import TimelineTab from "./carnet/TimelineTab";
import PhytoRegisterTab from "./carnet/PhytoRegisterTab";
import ChantiersTab from "./carnet/ChantiersTab";
import BiostimulantTab from "./carnet/BiostimulantTab";
import FertilisationTab from "./carnet/FertilisationTab";
import IrrigationTab from "./carnet/IrrigationTab";
import AnalyseSolTab from "./carnet/AnalyseSolTab";
import SanitaireTab from "./carnet/SanitaireTab";
import AmendaTab from "./carnet/AmendaTab";
import FertiRunTab from "./carnet/FertiRunTab";
import TravauxNotice from "./carnet/TravauxNotice";
import DuplicateCahierModal from "./carnet/DuplicateCahierModal";
import ItinerariesModal from "./carnet/ItinerariesModal";
import AnalyseIATab from "./carnet/AnalyseIATab";
import MesDonneesTab from "./carnet/MesDonneesTab";
import DocumentsTab from "./carnet/DocumentsTab";
import CompatibilityTab from "./carnet/CompatibilityTab";
import CulturesSection from "./carnet/CulturesSection";
import IlotsBar from "./carnet/IlotsBar";
import CadastreRefsLine from "@/components/carnet/CadastreRefsLine";

const API_BASE_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Onglets du quotidien (visibles) vs outils occasionnels (menu "Plus")
const MAIN_TABS = [
  { value: "timeline", label: "Chronologie", icon: Clock },
  { value: "phyto", label: "Registre Phyto", icon: Leaf },
  { value: "biostimulant", label: "Biostimulants", icon: Droplets },
  { value: "fertilisation", label: "Fertilisation", icon: Sprout },
  { value: "irrigation", label: "Irrigation", icon: Waves },
  { value: "sanitaire", label: "Sanitaire", icon: Bug },
  { value: "chantiers", label: "Chantiers", icon: Wrench },
];

// AMENDA et Ferti-Run en pause (26/10/2026, demande d'Anthony) le temps de rétablir la connexion avec le CIRAD :
// les onglets restent visibles avec un message. Repasser à false pour les réactiver.
const AMENDA_EN_TRAVAUX = true;

const MORE_TABS = [
  { value: "sol", label: "Analyses de Sol", icon: Beaker },
  { value: "compatibility", label: "Compatibilité", icon: FlaskConical },
  { value: "amenda", label: "AMENDA", icon: Calculator },
  { value: "fertirun", label: "Ferti-Run maraîchage", icon: Carrot },
  { value: "ia", label: "Analyse IA", icon: Sparkles },
  { value: "donnees", label: "Mes données", icon: Pencil },
  { value: "documents", label: "Documents", icon: FileText },
];

export default function CarnetParcelle({ parcel, refData: refDataProp, onClose, onBack, hideHeader, initialTab, phytoAutoOpen, phytoFilter }) {
  const [activeTab, setActiveTab] = useState(initialTab || "timeline");
  const [refData, setRefData] = useState(refDataProp || null);
  const [showDuplicate, setShowDuplicate] = useState(false);
  const closeDuplicate = useCallback(() => setShowDuplicate(false), []);
  const [showItineraries, setShowItineraries] = useState(false);
  // Incrémenté après l'ajout d'un itinéraire : remonte les onglets pour recharger leurs saisies
  const [carnetVersion, setCarnetVersion] = useState(0);
  const backHandler = onBack || onClose;
  const activeMoreTab = MORE_TABS.find(t => t.value === activeTab);
  // Culture principale à jour après édition dans CulturesSection (les onglets
  // reçoivent la nouvelle valeur sans recharger la parcelle).
  const [cultureOverride, setCultureOverride] = useState(null);
  useEffect(() => { setCultureOverride(null); }, [parcel?.parcel_id]);
  const [underGreenhouse, setUnderGreenhouse] = useState(!!parcel?.greenhouse);
  useEffect(() => { setUnderGreenhouse(!!parcel?.greenhouse); }, [parcel?.parcel_id, parcel?.greenhouse]);
  useEffect(() => {
    const onType = (e) => {
      if (e.detail?.parcelId === parcel?.parcel_id && "greenhouse" in e.detail) setUnderGreenhouse(!!e.detail.greenhouse);
    };
    window.addEventListener("veyer:parcel-type-changed", onType);
    return () => window.removeEventListener("veyer:parcel-type-changed", onType);
  }, [parcel?.parcel_id]);
  const cultureType = cultureOverride || parcel.culture_type;
  // Îlots (sous-parcelles) : filtre du carnet + rattachement des nouvelles saisies
  const [selectedIlotId, setSelectedIlotId] = useState(null);
  const [ilots, setIlots] = useState([]);
  useEffect(() => { setSelectedIlotId(null); setIlots([]); }, [parcel?.parcel_id]);
  // Données de calcul de l'îlot sélectionné (surface, culture, position) — repli sur la parcelle
  const selectedIlot = ilots.find(i => i.ilot_id === selectedIlotId) || null;
  const calcSurface = selectedIlot?.surface_ha || parcel.surface_ha;
  const calcCulture = selectedIlot?.culture || cultureType;
  const calcGps = (() => {
    const ring = selectedIlot?.geometry?.type === "Polygon" ? (selectedIlot.geometry.coordinates?.[0] || []) : [];
    const pts = ring.length > 1 ? ring.slice(0, -1) : ring;
    if (!pts.length) return null;
    return {
      lat: pts.reduce((s, c) => s + c[1], 0) / pts.length,
      lon: pts.reduce((s, c) => s + c[0], 0) / pts.length,
    };
  })();
  const calcParcel = selectedIlot ? { ...parcel, surface_ha: calcSurface, culture_type: calcCulture } : parcel;
  const cultureNames = (Array.isArray(parcel.cultures) && parcel.cultures.length > 0
    ? parcel.cultures.map(c => c.name)
    : [cultureType]).filter(Boolean);

  useEffect(() => {
    if (refDataProp || !parcel) return;
    // Détecte la région à partir du parcel_id (RE-* ou YT-*), fallback localStorage
    let region = "reunion";
    if (parcel?.region === "mayotte" || (parcel?.parcel_id || "").startsWith("YT-")) {
      region = "mayotte";
    } else if (parcel?.region === "reunion" || (parcel?.parcel_id || "").startsWith("RE-")) {
      region = "reunion";
    } else {
      try {
        const stored = window.localStorage.getItem("veyer_region");
        if (stored === "mayotte") region = "mayotte";
      } catch { /* ignore */ }
    }
    authFetch(`${API_BASE_URL}/carnet/reference-data?region=${region}`)
      .then(r => r.json())
      .then(setRefData)
      .catch(() => {});
  }, [parcel, refDataProp]);

  return (
    <div className="h-full flex-1 min-h-0 flex flex-col bg-white" data-testid="carnet-parcelle">
      {!hideHeader && (
        <div className="shrink-0 p-4 border-b border-black/5">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={backHandler} data-testid="carnet-back-btn">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="flex-1 min-w-0">
              <h2 className="text-sm font-bold tracking-tight truncate" style={{ fontFamily: 'Manrope, sans-serif', color: '#1A1A1A' }}>
                Carnet de Parcelle
              </h2>
              <p className="text-[11px] truncate" style={{ color: '#7A7A7A' }}>{parcel.name} — {parcel.surface_ha} ha</p>
              <CadastreRefsLine parcelId={parcel.parcel_id} />
            </div>
          </div>
        </div>
      )}

      {/* Cultures + îlots : hauteur plafonnée pour laisser la place aux onglets sur petit écran */}
      <div className="shrink-0 max-h-[45%] overflow-y-auto">
        {/* Cultures de la parcelle (principale + secondaires, production estimée) */}
        <CulturesSection parcel={parcel} onMainCultureChange={setCultureOverride} />

        {/* Îlots (sous-parcelles) : sélecteur + gestion */}
        <IlotsBar parcel={parcel} cultures={cultureNames} selectedIlotId={selectedIlotId} onSelect={setSelectedIlotId} onIlotsChanged={setIlots} />
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden min-h-0">
        <TabsList className="px-3 pt-2 bg-transparent justify-start gap-1 shrink-0 flex-wrap h-auto">
          {MAIN_TABS.map(t => {
            const Icon = t.icon;
            return (
              <TabsTrigger key={t.value} value={t.value} className="text-[11px] h-auto py-1.5 px-2.5 rounded-full gap-1" data-testid={`tab-${t.value}`}>
                <Icon className="w-3 h-3" /> {t.label}
              </TabsTrigger>
            );
          })}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="tab-more-menu"
                className={`inline-flex items-center gap-1 text-[11px] py-1.5 px-2.5 rounded-full transition-colors ${activeMoreTab ? "bg-white shadow-sm font-medium text-emerald-800 border border-emerald-200" : "text-gray-500 hover:text-gray-800"}`}>
                {activeMoreTab ? (
                  <><activeMoreTab.icon className="w-3 h-3" /> {activeMoreTab.label}</>
                ) : (
                  <><MoreHorizontal className="w-3 h-3" /> Plus</>
                )}
                <ChevronDown className="w-2.5 h-2.5 opacity-60" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="z-[2100]">
              {MORE_TABS.map(t => {
                const Icon = t.icon;
                return (
                  <DropdownMenuItem key={t.value} onClick={() => setActiveTab(t.value)} data-testid={`more-tab-${t.value}`} className={`text-xs gap-2 ${activeTab === t.value ? "bg-emerald-50 text-emerald-800" : ""}`}>
                    <Icon className="w-3.5 h-3.5" /> {t.label}
                  </DropdownMenuItem>
                );
              })}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setShowItineraries(true)} data-testid="more-itineraries" className="text-xs gap-2">
                <ListOrdered className="w-3.5 h-3.5" /> Itinéraires techniques…
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setShowDuplicate(true)} data-testid="more-duplicate-cahier" className="text-xs gap-2">
                <Copy className="w-3.5 h-3.5" /> Dupliquer le cahier…
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </TabsList>

        <TabsContent value="timeline" className="flex-1 overflow-hidden min-h-0 mt-0">
          <TimelineTab key={`tl${carnetVersion}`} parcelId={parcel.parcel_id} ilotId={selectedIlotId} ilots={ilots} />
        </TabsContent>
        <TabsContent value="phyto" className="flex-1 overflow-hidden min-h-0 mt-0">
          <PhytoRegisterTab key={`ph${carnetVersion}`} onOpenItineraries={() => setShowItineraries(true)} parcelId={parcel.parcel_id} parcelName={parcel.name} refData={refData} surfaceHa={calcSurface} cultureType={calcCulture} ilotId={selectedIlotId} ilotName={selectedIlot?.name} gpsOverride={calcGps} underGreenhouse={underGreenhouse} autoOpenWizard={phytoAutoOpen} initialFilter={phytoFilter} />
        </TabsContent>
        <TabsContent value="biostimulant" className="flex-1 overflow-hidden min-h-0 mt-0">
          <BiostimulantTab key={`bi${carnetVersion}`} onOpenItineraries={() => setShowItineraries(true)} parcelId={parcel.parcel_id} refData={refData} surfaceHa={calcSurface} cultureType={calcCulture} ilotId={selectedIlotId} ilots={ilots} />
        </TabsContent>
        <TabsContent value="fertilisation" className="flex-1 overflow-hidden min-h-0 mt-0">
          <FertilisationTab key={`fe${carnetVersion}`} onOpenItineraries={() => setShowItineraries(true)} parcelId={parcel.parcel_id} refData={refData} surfaceHa={calcSurface} cultureType={calcCulture} ilotId={selectedIlotId} ilots={ilots} />
        </TabsContent>
        <TabsContent value="irrigation" className="flex-1 overflow-hidden min-h-0 mt-0">
          <IrrigationTab key={`ir${carnetVersion}`} parcelId={parcel.parcel_id} surfaceHa={calcSurface} ilotId={selectedIlotId} ilots={ilots} />
        </TabsContent>
        <TabsContent value="chantiers" className="flex-1 overflow-hidden min-h-0 mt-0">
          <ChantiersTab parcelId={parcel.parcel_id} parcelName={parcel.name} ilotId={selectedIlotId} ilots={ilots} />
        </TabsContent>
        <TabsContent value="sol" className="flex-1 overflow-hidden min-h-0 mt-0">
          <AnalyseSolTab parcelId={parcel.parcel_id} refData={refData} ilotId={selectedIlotId} ilots={ilots} />
        </TabsContent>
        <TabsContent value="sanitaire" className="flex-1 overflow-hidden min-h-0 mt-0">
          <SanitaireTab parcelId={parcel.parcel_id} refData={refData} ilotId={selectedIlotId} ilots={ilots} />
        </TabsContent>
        <TabsContent value="compatibility" className="flex-1 overflow-hidden min-h-0 mt-0">
          <CompatibilityTab parcelId={parcel.parcel_id} ilotId={selectedIlotId} refData={refData} cultureType={calcCulture} />
        </TabsContent>
        <TabsContent value="amenda" className="flex-1 overflow-hidden min-h-0 mt-0">
          {AMENDA_EN_TRAVAUX ? (
            <TravauxNotice title="AMENDA est en travaux" testId="amenda-travaux">
              Nous rétablissons la connexion avec l'outil AMENDA du CIRAD pour vous proposer ses vrais calculs de fertilisation.
            </TravauxNotice>
          ) : <AmendaTab key={selectedIlotId || "parcelle"} parcel={calcParcel} ilotId={selectedIlotId} />}
        </TabsContent>
        <TabsContent value="fertirun" className="flex-1 overflow-hidden min-h-0 mt-0">
          {AMENDA_EN_TRAVAUX ? (
            <TravauxNotice title="Ferti-Run est en travaux" testId="fertirun-travaux">
              Nous rétablissons la connexion avec le service de calcul Ferti-Run (Chambre d'agriculture et CIRAD) pour le maraîchage.
            </TravauxNotice>
          ) : <FertiRunTab parcelId={parcel.parcel_id} ilotId={selectedIlotId} surfaceHa={calcSurface} cultureType={calcCulture} />}
        </TabsContent>
        <TabsContent value="ia" className="flex-1 overflow-hidden min-h-0 mt-0">
          <AnalyseIATab parcelId={parcel.parcel_id} />
        </TabsContent>
        <TabsContent value="donnees" className="flex-1 overflow-hidden min-h-0 mt-0">
          <MesDonneesTab parcelId={parcel.parcel_id} />
        </TabsContent>
        <TabsContent value="documents" className="flex-1 overflow-hidden min-h-0 mt-0">
          <DocumentsTab parcelId={parcel.parcel_id} parcelName={parcel.name} />
        </TabsContent>
      </Tabs>
      {showItineraries && (
        <ItinerariesModal
          parcelId={parcel.parcel_id} parcelName={parcel.name} ilotId={selectedIlotId} ilots={ilots}
          parcelSurface={parcel.surface_ha} parcelCulture={cultureType} refData={refData}
          onClose={() => setShowItineraries(false)} onApplied={() => setCarnetVersion((v) => v + 1)}
        />
      )}
      {showDuplicate && (
        <DuplicateCahierModal parcelId={parcel.parcel_id} ilotId={selectedIlotId} onClose={closeDuplicate} />
      )}
    </div>
  );
}
